import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ErrorCode } from '../common/error-codes/ErrorCode';
import { SnackBarService } from '../shared/services/snack-bar.service';
import { AuthService } from '../core/authentication/auth.service';
import { BYPASS_ERROR_HANDLER } from '../common/utils/http-context-tokens';

@Injectable()
export class ErrorHandlingInterceptor implements HttpInterceptor {
  private injector = inject(Injector);

  constructor() { }

  private get snackbar(): SnackBarService {
    return this.injector.get(SnackBarService);
  }
  private get auth(): AuthService {
    return this.injector.get(AuthService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.context.get(BYPASS_ERROR_HANDLER)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.error === ErrorCode.UserDoesNotHaveWorkerProfile) {
          return throwError(() => error);
        }

        if (error.status === 401) {
          if (!this.auth.isSignOutRequested) {
            window.location.reload();
          }
          return throwError(() => error);
        }

        if (error.status === 403) {
          this.snackbar.openErrorSnackBar(ErrorCode.AccessDenied);
          return throwError(() => error);
        }

        if (!(error.error instanceof Blob)) {
          this.openSnackbar(error);
        } else {
          this.readBlobErrorMessage(error).then(err => this.openSnackbar(err));
        }

        return throwError(() => error);
      })
    );
  }

  private openSnackbar(error: any): void {
    if (new RegExp('^[a-zA-Z,\\s]+$').test(error.error) && typeof error.error === 'string') {
      const errors: string[] = error.error.replace(/\s/g, '').split(',');
      this.snackbar.openErrorMultipleSnackBarInRow(errors);
    } else if (!environment.production && typeof error.error === 'string') {
      this.snackbar.openErrorSnackBar(error.error);
    } else if (environment.production && typeof error.error === 'string' && new RegExp('^[a-zA-Z]+$').test(error.error)) {
      this.snackbar.openErrorSnackBar(error.error);
    } else {
      this.snackbar.openErrorSnackBar(ErrorCode.SomethingWentWrong);
    }
  }

  private readBlobErrorMessage(err: HttpErrorResponse): Promise<HttpErrorResponse> {
    return new Promise<HttpErrorResponse>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: Event) => {
        try {
          resolve(
            new HttpErrorResponse({
              error: (e.target as FileReader).result,
              headers: err.headers,
              status: err.status,
              statusText: err.statusText,
              url: err.url
            })
          );
        } catch {
          reject(err);
        }
      };
      reader.onerror = () => reject(err);
      reader.readAsText(err.error as Blob);
    });
  }
}